import React, { useEffect, useState } from "react";
import ActivityFormWrapper from "@components/activities/ActivityFormWrapper";
import showToast from "@utils/toast";
import { useParams } from "react-router-dom";
import {
  ActivityMeta,
  ActivityType,
  GetActivity,
  GetActivityApiResponse,
  ILesson,
  ILevel,
} from "src/types";
import Layout from "@components/layout";
import { getRequest } from "src/service/api-services/api-services";
import { API_URL } from "src/service/config";
import {
  fetchLessonsByLevel,
  listLevels,
} from "src/service/activities.services";

const supportedActivityTypes: { label: string; value: ActivityType }[] = [
  { label: "Video Content", value: "VideoContentActivity" },
  { label: "Select Image", value: "SelectCorrectImageActivity" },
  { label: "Tap what you Hear", value: "TapInWhatYouHear" },
  { label: "Fill the Gap", value: "FillTheGapActivity" },
  { label: "Match The Correct Word", value: "MatchTheCorrectWord" },
  { label: "Cross word match match", value: "CrossWordActivity" },
];

const supportedLanguages = [
  { label: "Hausa", value: "hausa" },
  { label: "Igbo", value: "igbo" },
  { label: "Yoruba", value: "yoruba" },
];

const EditActivityPage = () => {
  const { activityType, activityId } = useParams();
  const [loading, setLoading] = useState(false);
  const [activity, setActivity] = useState<GetActivity | null>(null);
  const [levelList, setLevelList] = useState<ILevel[] | []>([]);
  const [lessonList, setLessonList] = useState<ILesson[] | []>([]);
  const [lessonMeta, setLessonMeta] = useState<ActivityMeta>({
    language: "",
    level: "",
    lesson: "",
  });

  const handleMetaChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    setLessonMeta({ ...lessonMeta, [e.target.name]: e.target.value });
  };

  const fetchActivity = async () => {
    try {
      const response = await getRequest<GetActivityApiResponse>(
        `${API_URL}/interactive-activities/${activityId}`
      );

      setActivity(response.result);
    } catch {
      showToast("Can't load activity at the moment!", false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchActivity();
  }, [activityId, activityType]);

  // Fetch lesson based on language
  const fetchLevelsByLanguage = async () => {
    try {
      const level = lessonMeta.language;
      if (!level) {
        return null;
      }
      const levels = await listLevels(level);
      if (levels.status === "success") setLevelList(levels.result);
    } catch (error: any) {
      setLevelList([]);
      showToast(error?.message || "Failed to fetch levels", false);
    }
  };

  const fetchLessonByLanguage = async () => {
    try {
      const { level, language } = lessonMeta;
      if (!level) {
        return null;
      }

      if (!language) return null;

      const lessons = await fetchLessonsByLevel(level, language);
      if (lessons.status !== "success") {
        showToast("There was error fetching lesson!", false);
      } else {
        setLessonList(lessons.result);
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error?.message
          : "Apologies, an error occured!";
      setLessonList([]);
      showToast(`${message}`, false);
    }
  };

  useEffect(() => {
    fetchLevelsByLanguage();
    fetchLessonByLanguage();
  }, [lessonMeta.language, lessonMeta.level]);

  if (loading) return <p className="p-6">Loading...</p>;
  if (!activity)
    return (
      <p className="p-6 font-bold text-xl items-center">Activity not found.</p>
    );

  return (
    <Layout>
      {/* <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-xl shadow-md p-8 space-y-8">
          <div className="text-center space-y-1">
            <h1 className="text-2xl font-bold text-gray-800">
              Edit Lesson Activity
            </h1>
            <p className="text-sm text-gray-500">
              Define language, level, and select an activity type
            </p>
          </div>
          <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
            <div className="space-y-6">
              <h2 className="text-base font-semibold text-gray-700 border-b pb-2">
                Metadata
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="language"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Language
                  </label>
                  <select
                    id="language"
                    name="language"
                    value={lessonMeta.language}
                    onChange={handleMetaChange}
                    className="input-style mt-1"
                    required
                  >
                    <option value="" disabled>
                      {" "}
                      Select Language{" "}
                    </option>{" "}
                    {supportedLanguages.map(({ label, value }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="level"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Level
                  </label>
                  <select
                    id="level"
                    name="level"
                    value={lessonMeta.level}
                    onChange={handleMetaChange}
                    className="input-style mt-1"
                    required
                  >
                    <option value="" disabled>
                      {" "}
                      Select level{" "}
                    </option>{" "}
                    {levelList.map((level) => (
                      <option key={level.id} value={level.id}>
                        {level.level} - {level.title}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sm:col-span-2">
                  <label
                    htmlFor="lesson"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Lesson
                  </label>
                  <select
                    id="lesson"
                    name="lesson"
                    value={lessonMeta.lesson}
                    onChange={handleMetaChange}
                    className="input-style mt-1"
                    required
                  >
                    <option value="" disabled>
                      {" "}
                      Select Lesson{" "}
                    </option>{" "}
                    {lessonList.map((lesson) => (
                      <option key={lesson.id} value={lesson.id}>
                        {lesson.title}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div> */}

      <div className="pt-5">
      <ActivityFormWrapper
        activityType={activityType as ActivityType}
        initialActivity={activity}
      />
        </div>
      {/* </div>
        </div> */}
      {/* </div> */}
    </Layout>
  );
};

export default EditActivityPage;
